import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import * as authApi from '../api/auth';
import { AUTH_TOKEN_STORAGE_KEY } from '../api/client';
import pawImg from '../assets/paw.png';

const Confirmed: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  // Hide navigation on this page to avoid showing wrong auth state
  useEffect(() => {
    const nav = document.querySelector('.vertical-menu');
    const authCorner = document.querySelector('.auth-corner');
    if (nav) (nav as HTMLElement).style.display = 'none';
    if (authCorner) (authCorner as HTMLElement).style.display = 'none';
    
    return () => {
      if (nav) (nav as HTMLElement).style.display = '';
      if (authCorner) (authCorner as HTMLElement).style.display = '';
    };
  }, []);

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setErrorMessage('No confirmation token provided.');
      return;
    }

    const confirm = async () => {
      try {
        // Store the token directly
        localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
        
        // The interceptor will add Bearer prefix automatically
        await authApi.me();
        
        // Refresh user data in context
        await refreshUser();
        
        // Clear welcome banner dismissed flag so it shows for new users
        localStorage.removeItem('chowmedown_welcome_dismissed');
        
        setStatus('success');
        
        // Redirect to recipes page after 2 seconds with a full page reload to update auth state
        setTimeout(() => {
          window.location.href = '/recipes';
        }, 2000);
      } catch (err: any) {
        console.error('Confirmation error:', err);
        const message = err?.response?.data?.error || err?.message || 'Confirmation failed';
        setErrorMessage(message);
        setStatus('error');
        
        // Redirect to error page after 2 seconds
        setTimeout(() => {
          navigate('/confirmation-error', { replace: true });
        }, 2000);
      }
    };

    confirm();
  }, [searchParams, navigate, refreshUser]);

  return (
    <div className="page-container cream-bg">
      <div className="hero-paw-bg">
        <img src={pawImg} className="hero-paw-float paw1" alt="" />
        <img src={pawImg} className="hero-paw-float paw2" alt="" />
        <img src={pawImg} className="hero-paw-float paw3" alt="" />
        <img src={pawImg} className="hero-paw-float paw4" alt="" />
        <img src={pawImg} className="hero-paw-float paw5" alt="" />
        <img src={pawImg} className="hero-paw-float paw6" alt="" />
        <img src={pawImg} className="hero-paw-float paw7" alt="" />
      </div>

      <div style={{ maxWidth: 560, margin: '0 auto', padding: '2rem', textAlign: 'center' }}>
        <div className="page-intro" style={{ padding: 0 }}>
          {status === 'loading' && (
            <>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>⏳</div>
              <h1 className="page-title">Confirming your email...</h1>
              <div className="page-intro-underline" />
              <p style={{ marginTop: '1rem', color: '#666', fontSize: '1rem' }}>
                Please wait while we verify your account.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div style={{ marginBottom: '1rem' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" fill="#16a34a" viewBox="0 0 256 256">
                  <path d="M173.66,98.34a8,8,0,0,1,0,11.32l-56,56a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L112,148.69l50.34-50.35A8,8,0,0,1,173.66,98.34ZM232,128A104,104,0,1,1,128,24,104.11,104.11,0,0,1,232,128Zm-16,0a88,88,0,1,0-88,88A88.1,88.1,0,0,0,216,128Z"></path>
                </svg>
              </div>
              <h1 className="page-title">Your email is confirmed!</h1>
              <div className="page-intro-underline" />
              <p style={{ marginTop: '1rem', color: '#16a34a', fontSize: '1.1rem', fontWeight: 600 }}>
                Welcome to Chow Me Down - let's get cooking for your pup!
              </p>
              <p style={{ marginTop: '0.5rem', color: '#666', fontSize: '1rem' }}>
                Redirecting you now, hang tight!
              </p>
              <p style={{ marginTop: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
                If nothing happens, <a href="/recipes" style={{ color: '#c14444', textDecoration: 'underline' }}>go to recipes page</a>
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>❌</div>
              <h1 className="page-title">Confirmation failed</h1>
              <div className="page-intro-underline" />
              <p style={{ marginTop: '1rem', color: '#b24646', fontSize: '1rem', fontWeight: 600 }}>
                {errorMessage}
              </p>
              <p style={{ marginTop: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
                Redirecting to help page...
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Confirmed;
